import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Clock, Package as PackageIcon, X } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, LoadingState } from '@/components/ui/states';
import { useCurrency } from '@/Hooks/useCurrency';
import { IssuedEntitlements } from './IssuedEntitlements';
import {
  RESOURCE_TYPES,
  type CatalogItem,
  type EntitlementType,
  type Package,
  apiError,
} from './lib';

interface Resource {
  id: string;
  name: string;
  type: string;
}

/**
 * The grant-type switch and the fields the chosen type needs. One definition,
 * used both by the create form (which composes `templates[]` before the package
 * exists) and by the detail pane (which POSTs one at a time to
 * `/packages/:id/templates`, because `UpdatePackageDto` omits templates).
 */
function TemplateFields({
  value,
  onChange,
  items,
  resources,
}: {
  value: TemplateDraft;
  onChange: (next: TemplateDraft) => void;
  items: CatalogItem[];
  resources: Resource[];
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t('catalog.packages.grantType')}>
        <Select
          className="w-56"
          value={value.type}
          onChange={(e) => onChange({ ...value, type: e.target.value as EntitlementType })}
        >
          <option value="CATALOG_ITEM">{t('catalog.packages.grant.CATALOG_ITEM')}</option>
          <option value="RESOURCE_TIME">{t('catalog.packages.grant.RESOURCE_TIME')}</option>
        </Select>
      </Field>

      {value.type === 'CATALOG_ITEM' ? (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('catalog.pricing.item')}>
            <Select
              className="w-56"
              value={value.catalogItemId}
              onChange={(e) => onChange({ ...value, catalogItemId: e.target.value })}
            >
              <option value="">{t('catalog.pricing.chooseItem')}</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.quantity')}>
            <Input
              className="w-24"
              value={value.quantity}
              onChange={(e) => onChange({ ...value, quantity: e.target.value })}
            />
          </Field>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('catalog.packages.minutes')}>
            <Input
              className="w-24"
              type="number"
              min="1"
              value={value.minutes}
              onChange={(e) => onChange({ ...value, minutes: e.target.value })}
            />
          </Field>
          <Field label={t('catalog.packages.resource')}>
            <Select
              className="w-52"
              value={value.resourceId}
              onChange={(e) => onChange({ ...value, resourceId: e.target.value })}
            >
              <option value="">{t('catalog.packages.anyResource')}</option>
              {resources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.name}
                </option>
              ))}
            </Select>
          </Field>
          {!value.resourceId && (
            <Field label={t('catalog.packages.resourceType')}>
              <Select
                className="w-52"
                value={value.resourceType}
                onChange={(e) => onChange({ ...value, resourceType: e.target.value })}
              >
                <option value="">{t('catalog.packages.anyType')}</option>
                {RESOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`catalog.resourceType.${type}`)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
      )}
    </>
  );
}

const BLANK = {
  name: '',
  description: '',
  price: '0.00',
  validityMinutes: '',
  catalogItemId: '',
  isActive: true,
};

const BLANK_TEMPLATE = {
  type: 'CATALOG_ITEM' as EntitlementType,
  catalogItemId: '',
  quantity: '1',
  resourceId: '',
  resourceType: '',
  minutes: '60',
};

type TemplateDraft = typeof BLANK_TEMPLATE;

/**
 * One `CreateEntitlementTemplateDto`. Fields the chosen type does not use are
 * omitted rather than sent blank — the property is on the DTO, so
 * `forbidNonWhitelisted` lets it through, and then `@IsString()` refuses `''`.
 */
function templateBody(draft: TemplateDraft) {
  return draft.type === 'CATALOG_ITEM'
    ? {
        type: 'CATALOG_ITEM',
        catalogItemId: draft.catalogItemId,
        quantity: draft.quantity || '1',
      }
    : {
        type: 'RESOURCE_TIME',
        minutes: Number(draft.minutes) || 0,
        resourceId: draft.resourceId || undefined,
        resourceType: draft.resourceId ? undefined : draft.resourceType || undefined,
      };
}

/** Whether this line is still missing the one thing its type needs. */
function draftIncomplete(draft: TemplateDraft): boolean {
  return draft.type === 'CATALOG_ITEM' ? !draft.catalogItemId : !(Number(draft.minutes) > 0);
}

export default function PackagesTab({ items }: { items: CatalogItem[] }) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  // I-06 — authoring a package is its own grant now, not `menu.*`.
  const canCreate = can('packages.create');
  const canUpdate = can('packages.update');
  const canDelete = can('packages.delete');

  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Package | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Package | null>(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const [template, setTemplate] = useState(BLANK_TEMPLATE);

  /**
   * The create form's included items, held client-side until the package
   * exists. `CreatePackageDto` takes `templates[]`, so a new package and its
   * contents are ONE request — which is why the frame can draw "Included items"
   * on a create screen at all. Editing is different: `UpdatePackageDto` omits
   * `templates` deliberately, so the detail pane keeps its own per-line POSTs.
   */
  const [drafts, setDrafts] = useState<TemplateDraft[]>([]);
  const [draft, setDraft] = useState(BLANK_TEMPLATE);

  const describeDraft = (line: TemplateDraft): string =>
    line.type === 'CATALOG_ITEM'
      ? t('catalog.packages.itemGrant', {
          quantity: line.quantity || '1',
          name: items.find((item) => item.id === line.catalogItemId)?.name ?? '',
        })
      : t('catalog.packages.minutesOn', {
          minutes: Number(line.minutes) || 0,
          target:
            resources.find((resource) => resource.id === line.resourceId)?.name ??
            (line.resourceType
              ? t(`catalog.resourceType.${line.resourceType}`)
              : t('catalog.packages.anyResource')),
        });

  const loadPackages = useCallback(() => {
    setLoading(true);
    api
      .get<{ data: Package[] }>('/packages', { params: { limit: 100 } })
      .then(({ data }) => {
        const rows = data.data ?? [];
        setPackages(rows);
        setSelectedId((current) => current ?? rows[0]?.id ?? null);
      })
      .catch((e) => toast.error(apiError(e, t('catalog.packages.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(loadPackages, [loadPackages]);

  useEffect(() => {
    // Resources need `resources.view`; without it only catalog-item grants can
    // be defined here.
    api
      .get<{ data: Resource[] }>('/resources', { params: { limit: 200 } })
      .then(({ data }) => setResources(data.data ?? []))
      .catch(() => setResources([]));
  }, []);

  const loadDetail = useCallback(
    (id: string) => {
      api
        .get<Package>(`/packages/${id}`)
        .then(({ data }) => setDetail(data))
        .catch((e) => toast.error(apiError(e, t('catalog.packages.loadFailed'))));
    },
    [t],
  );

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setDrafts([]);
    setDraft(BLANK_TEMPLATE);
    setFormOpen(true);
  };

  const openEdit = (pkg: Package) => {
    setEditing(pkg);
    setDrafts([]);
    setDraft(BLANK_TEMPLATE);
    setForm({
      name: pkg.name,
      description: pkg.description ?? '',
      price: pkg.price,
      validityMinutes: pkg.validityMinutes ? String(pkg.validityMinutes) : '',
      catalogItemId: pkg.catalogItemId ?? '',
      isActive: pkg.isActive,
    });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    // `price` is a decimal string on the wire — a JSON float cannot hold 0.1.
    const body = {
      name: form.name,
      description: form.description || undefined,
      price: form.price || '0',
      validityMinutes: form.validityMinutes ? Number(form.validityMinutes) : undefined,
      catalogItemId: form.catalogItemId || undefined,
      isActive: form.isActive,
    };
    try {
      if (editing) await api.patch(`/packages/${editing.id}`, body);
      else {
        const { data } = await api.post<Package>('/packages', {
          ...body,
          ...(drafts.length ? { templates: drafts.map(templateBody) } : {}),
        });
        setSelectedId(data.id);
      }
      toast.success(t(editing ? 'catalog.packages.updated' : 'catalog.packages.created'));
      setFormOpen(false);
      loadPackages();
    } catch (e) {
      toast.error(apiError(e, t('catalog.packages.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const removePackage = async (pkg: Package) => {
    if (!(await confirm({ title: t('catalog.packages.deleteConfirm', { name: pkg.name }), tone: 'danger' }))) return;
    try {
      await api.delete(`/packages/${pkg.id}`);
      toast.success(t('catalog.packages.deleted'));
      setSelectedId(null);
      setDetail(null);
      loadPackages();
    } catch (e) {
      toast.error(apiError(e, t('catalog.packages.saveFailed')));
    }
  };

  const addTemplate = async () => {
    if (!detail) return;
    try {
      await api.post(`/packages/${detail.id}/templates`, templateBody(template));
      setTemplate(BLANK_TEMPLATE);
      loadDetail(detail.id);
      loadPackages();
    } catch (e) {
      toast.error(apiError(e, t('catalog.packages.saveFailed')));
    }
  };

  const removeTemplate = async (templateId: string) => {
    if (!detail) return;
    try {
      await api.delete(`/packages/${detail.id}/templates/${templateId}`);
      loadDetail(detail.id);
      loadPackages();
    } catch (e) {
      toast.error(apiError(e, t('catalog.packages.saveFailed')));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[17rem_1fr]">
      <Card className="h-fit">
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle>{t('catalog.packages.title')}</CardTitle>
          {canCreate && (
            <Button size="sm" onClick={openCreate}>
              {t('common.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : packages.length === 0 ? (
            <EmptyState title={t('catalog.packages.empty')} hint={t('catalog.packages.emptyHint')} />
          ) : (
            <ul className="divide-y rounded-lg border">
              {packages.map((pkg) => (
                <li key={pkg.id}>
                  <button
                    onClick={() => setSelectedId(pkg.id)}
                    className={`w-full p-3 text-start text-sm hover:bg-hover ${
                      pkg.id === selectedId ? 'bg-accent-soft font-medium text-accent-soft-fg' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{pkg.name}</span>
                      <span>{format(pkg.price)}</span>
                    </div>
                    <span className="text-xs text-ink-muted">
                      {t('catalog.packages.entitlementCount', {
                        count: pkg.entitlements?.length ?? 0,
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {!detail ? (
        <Card>
          <CardContent className="p-5">
            <EmptyState title={t('catalog.packages.nothingSelected')} />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3">
            <div>
              <CardTitle>{detail.name}</CardTitle>
              <p className="text-xs text-ink-muted">
                {format(detail.price)} ·{' '}
                {detail.validityMinutes
                  ? t('catalog.packages.validFor', { minutes: detail.validityMinutes })
                  : t('catalog.packages.visitOnly')}
                {detail.catalogItem ? ` · ${detail.catalogItem.name}` : ''}
              </p>
              {detail.description && <p className="mt-1 text-sm">{detail.description}</p>}
            </div>
            <div className="flex gap-2">
              {canUpdate && (
                <Button size="sm" variant="secondary" onClick={() => openEdit(detail)}>
                  {t('common.edit')}
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="danger" onClick={() => removePackage(detail)}>
                  {t('common.delete')}
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold">{t('catalog.packages.templates')}</h4>
              <p className="text-xs text-ink-muted">
                {t('catalog.packages.templatesHint')}
              </p>
            </div>

            {(detail.entitlements ?? []).length === 0 ? (
              <p className="text-sm text-ink-muted">
                {t('catalog.packages.noTemplates')}
              </p>
            ) : (
              <ul className="divide-y rounded-lg border">
                {detail.entitlements?.map((entitlement) => (
                  <li
                    key={entitlement.id}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      {entitlement.type === 'RESOURCE_TIME' ? (
                        <Clock className="h-4 w-4 text-accent" />
                      ) : (
                        <PackageIcon className="h-4 w-4 text-accent" />
                      )}
                      {entitlement.type === 'RESOURCE_TIME' ? (
                        <span>
                          {t('catalog.packages.minutesOn', {
                            minutes: entitlement.minutes ?? 0,
                            target:
                              entitlement.resource?.name ??
                              (entitlement.resourceType
                                ? t(`catalog.resourceType.${entitlement.resourceType}`)
                                : t('catalog.packages.anyResource')),
                          })}
                        </span>
                      ) : (
                        <span>
                          {t('catalog.packages.itemGrant', {
                            quantity: entitlement.quantity ?? '1',
                            name: entitlement.catalogItem?.name ?? entitlement.catalogItemId ?? '',
                          })}
                        </span>
                      )}
                      <Badge tone="neutral">{t('catalog.packages.template')}</Badge>
                    </span>
                    {canUpdate && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeTemplate(entitlement.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canUpdate && (
              <div className="space-y-3 rounded-lg border p-4">
                <TemplateFields
                  value={template}
                  onChange={setTemplate}
                  items={items}
                  resources={resources}
                />
                <Button size="sm" onClick={addTemplate} disabled={draftIncomplete(template)}>
                  {t('catalog.packages.addTemplate')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* C-09 — the issued side of the package: who holds one, what is left on
          it, and the void path. Full width under the definition editor. */}
      <div className="lg:col-span-2">
        <IssuedEntitlements pkg={detail} />
      </div>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'catalog.packages.editPackage' : 'catalog.packages.newPackage')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving || !form.name.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">
              {t('catalog.packages.detailsSection')}
            </h3>
            <Field label={t('catalog.items.name')} required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label={t('catalog.items.description')}>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              {t('catalog.items.active')}
            </label>
          </section>

          <section className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold">
              {t('catalog.packages.pricingSection')}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('catalog.packages.price')}>
                <Input
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </Field>
              <Field
                label={t('catalog.packages.validityMinutes')}
                hint={t('catalog.packages.validityHint')}
              >
                <Input
                  type="number"
                  min="1"
                  value={form.validityMinutes}
                  onChange={(e) => setForm({ ...form, validityMinutes: e.target.value })}
                />
              </Field>
            </div>
            {/* The link that makes a package sellable at all — without it the
                package has no price on any order line. The frame omits it. */}
            <Field label={t('catalog.packages.soldAs')} hint={t('catalog.packages.soldAsHint')}>
              <Select
                value={form.catalogItemId}
                onChange={(e) => setForm({ ...form, catalogItemId: e.target.value })}
              >
                <option value="">{t('catalog.packages.noCatalogItem')}</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          </section>

          {!editing && (
            <section className="space-y-3 border-t pt-4">
              <div>
                <h3 className="text-sm font-semibold">{t('catalog.packages.templates')}</h3>
                <p className="text-xs text-ink-muted">{t('catalog.packages.emptyHint')}</p>
              </div>

              {drafts.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {drafts.map((line, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-2 rounded-lg border px-2 py-1 text-sm"
                    >
                      <span>{describeDraft(line)}</span>
                      <button
                        type="button"
                        className="rounded p-0.5 text-ink-muted hover:bg-hover"
                        aria-label={t('common.delete')}
                        onClick={() => setDrafts(drafts.filter((_, at) => at !== index))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="space-y-3 rounded-lg border p-4">
                <TemplateFields
                  value={draft}
                  onChange={setDraft}
                  items={items}
                  resources={resources}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={draftIncomplete(draft)}
                  onClick={() => {
                    setDrafts([...drafts, draft]);
                    setDraft(BLANK_TEMPLATE);
                  }}
                >
                  {t('catalog.packages.addTemplate')}
                </Button>
              </div>
            </section>
          )}
        </div>
      </Dialog>
    </div>
  );
}
